// Every user-facing string in the client, in one module.
//
// **The copy quoted in PLAN.md is binding as written** and tests assert against it. It lives
// here rather than inline in a component so that "does the shell still say what the plan says"
// is one grep, not twelve.

export const COPY = {
  /** §6.4 — shown once, the first time the browser eats a chord a desktop wants. */
  stolenChord:
    'Your browser took that shortcut. Press F11 for immersive mode to give it to Hangar.',

  /** §18 F12 — session restore found a window whose app is no longer installed. */
  sessionSkipped: (count: number) =>
    count === 1
      ? 'One window couldn’t be restored — its app is no longer installed'
      : `${count} windows couldn’t be restored — their apps are no longer installed`,

  /** §9 / D-024 — the persistent guest chip in the tray. */
  guestChip: 'Guest session — files live in this browser only · Export · Sign in',
  /** §7.6 — the once-per-session guest banner. */
  guestBanner: 'Guest session — files live in this browser only',
  guestBannerExport: 'Export',
  guestBannerSignIn: 'Sign in',

  /** §18 F1 — signed in, server unreachable. */
  offline: 'Offline — changes will sync when you’re back',
  /** §18 F2 — Authentik unreachable. */
  signInUnavailable: 'Sign-in is unavailable right now',

  /** §8.3 — the whole page when an admin has turned anonymous access off. */
  loginTitle: 'Hangar',
  loginBody: 'This desktop is available to signed-in users.',
  loginAction: 'Sign in',
  /** §11.2 — the Terminal. */
  terminal: {
    banner: 'Hangar Terminal — type `help` for the commands it knows',
    newTab: 'New tab',
    closeTab: 'Close tab',
    newWindow: 'New window',
    screen: 'Terminal',
  },

  /** §7.4 / D-045 — a conflict copy was made; §13 names both files. */
  syncConflictTitle: 'Both copies were kept',
  syncConflictBody: (path: string, copy: string) =>
    `${path.split('/').pop() ?? path} changed in two places — your version was saved as ${
      copy.split('/').pop() ?? copy
    }`,

  /** §18 F4 — a write failed for want of room. Both ways out are offered, not described. */
  quotaFull: 'This browser is out of space for Hangar. Export your files, or empty the Recycle Bin.',
  quotaExport: 'Export everything',
  quotaEmptyTrash: 'Empty the Recycle Bin',

  /** §18 F5 — OPFS evicted between sessions. */
  storageCleared: "This browser cleared Hangar's storage",

  /** §9.6 / §5.4 — an external app whose origin is down. */
  appNotResponding: (name: string) => `${name} isn't responding`,
  appRetry: 'Retry',
  appOpenInTab: 'Open in a tab',
  appUninstall: 'Uninstall',
  /** §18 F10 — a `resumable` app that was unloaded while minimized, coming back. */
  appResuming: 'Picking up where you left off…',

  /** §11.4 — a site that refuses to be framed. */
  framingRefused: "This site doesn't allow embedding",
  framingOpenInNewTab: 'Open in a new tab',

  /** §11.7 — Photos. */
  photos: {
    empty: 'Open an image to view it',
    unreadable: 'That image could not be read',
    previous: 'Previous',
    next: 'Next',
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    fit: 'Fit',
    slideshow: 'Slideshow',
    slideshowStop: 'Stop',
    setWallpaper: 'Set as wallpaper',
    wallpaperSet: 'Wallpaper set',
    share: 'Share',
    shared: 'Shared',
    /** D-018 — Hangar has no raster editor; Edit hands the image to one that is installed. */
    edit: 'Edit',
    sentToEditor: 'Sent to the editor',
    shareCancelled: 'Nothing was sent',
  },

  /** §11.6 — Settings, the pages beyond Personalization. */
  settings: {
    pages: {
      personalization: 'Personalization',
      sounds: 'Sounds',
      keyboard: 'Keyboard',
      defaultApps: 'Default apps',
      storage: 'Storage',
      apps: 'Apps',
      account: 'Account',
      about: 'About',
    },
    /** Keyboard (§6.4). */
    keyboardHint: 'Click a shortcut, then press the keys you want.',
    recording: 'Press the keys…',
    change: 'Change',
    resetBinding: 'Reset',
    reservedTag: 'Claimed by Hangar',
    bindingConflict: 'Another shortcut already uses that',
    bindingInvalid: 'That isn’t a shortcut',
    bindingBrowser: 'Your browser takes that one first',
    /** Default apps (§9.2). */
    defaultAppsHint: 'What opens each kind of file when you double-click it.',
    noCandidates: 'Nothing installed opens this',
    /** Storage (§7.6, §7.7). */
    exportEverything: 'Export everything',
    exportHint: 'A .zip of every file, whether or not you’re signed in',
    resetSession: 'Reset guest session',
    resetHint: 'Delete every file and setting in this browser',
    resetConfirm: 'Delete every file and setting stored in this browser? This cannot be undone.',
    resetDone: 'This browser’s session has been reset',
    /** Apps (§9.6). */
    noApps: 'Nothing installed yet — the Store has the catalogue',
    granted: 'Granted',
    notGranted: 'Asked for, not granted',
    health: (state: string) => (state === 'unhealthy' ? 'Not responding' : 'Reachable'),
    uninstall: 'Uninstall',
    /** Account (§8.3). */
    signedInAs: (name: string) => `Signed in as ${name}`,
    guestAccount: 'Guest session — files live in this browser only',
    signIn: 'Sign in',
    signOut: 'Sign out',
    anonymousToggle: 'Let anyone use this desktop without signing in',
    anonymousAdminOnly: 'Only an administrator can change this',
    anonymousNoServer: 'There’s no server to ask',
    anonymousFailed: 'That change was refused',
    /** About (§11.6, D-042). */
    version: (value: string) => `Hangar ${value}`,
    capabilityTitle: 'What this browser can do',
    capabilityYes: 'Yes',
    capabilityNo: 'No',
  },

  /**
   * §8 / §26.2 (D-346) — the launcher's profile row.
   *
   * *Active · Away · Busy* rather than the model's `online`/`idle`/`busy`: the stored words are
   * about a **connection** and these are about a **person**, and the person's vocabulary is the one
   * that belongs on screen. `offline` has no label here at all, because it is not a choice — it is
   * what the clock says about somebody who stopped checking in (§26.2).
   */
  profile: {
    guest: 'Guest',
    guestNote: 'Files live in this browser only',
    signIn: 'Sign in',
    signOut: 'Sign out',
    status: {
      online: 'Active',
      idle: 'Away',
      busy: 'Busy',
      offline: 'Offline',
    } as Readonly<Record<string, string>>,
  },

  /** §11.10 — Clock & Calendar. */
  clock: {
    now: 'Now',
    timers: 'Timers',
    calendar: 'Calendar',
    open: 'Open Clock & Calendar',
    durationLabel: 'Length',
    durationPlaceholder: '10m',
    durationHint: 'Minutes, or 90s · 2h · 1:30',
    nameLabel: 'Name',
    namePlaceholder: 'Timer',
    start: 'Start',
    cancel: 'Cancel',
    noTimers: 'No timers running',
    timerDone: (label: string) => `${label} finished`,
    timerDoneBody: (length: string) => `Your ${length} timer is up`,
    previousMonth: 'Previous month',
    nextMonth: 'Next month',
    today: 'Today',
    nothingOnDay: 'Nothing on this day',
    /** §11.10 — the calendar surface is where notifications land. */
    noticesOnDay: (count: number) => (count === 1 ? '1 notification' : `${count} notifications`),
  },

  /**
   * §11.14 — Alarms & Stopwatch.
   *
   * The one string that carries a promise is `closedTabNote`, and it says the limitation outright:
   * the schedule is the shell's, so it survives the app's window closing, and it stops when the
   * **tab** does. An alarm app that let somebody believe otherwise would be worse than no alarm
   * app — they would find out on the morning it mattered.
   */
  alarms: {
    title: 'Alarms & Stopwatch',
    tabAlarms: 'Alarms',
    tabStopwatch: 'Stopwatch',

    /** The list. */
    noAlarms: 'No alarms yet.',
    add: 'Add alarm',
    timeLabel: 'Time',
    timePlaceholder: '7:30',
    timeHint: '24-hour, or 7:30 am',
    labelLabel: 'Label',
    labelPlaceholder: 'Alarm',
    repeatLabel: 'Repeat',
    remove: 'Delete',
    /** §19.4 — deleting confirms, and says what is actually lost. */
    confirmDelete: (label: string) =>
      `Delete ${label.trim().length === 0 ? 'this alarm' : label}? It will not ring again.`,
    /** The toggle's accessible name — "Alarm on"/"Alarm off" reads wrong on a switch. */
    enabledLabel: (label: string) => `${label} on`,
    nextRing: (when: string) => `Next ${when}`,
    notArmed: 'Off',
    snoozedUntil: (when: string) => `Snoozed until ${when}`,
    rangAt: (when: string) => `Rang at ${when}`,
    closedTabNote:
      'Alarms keep running while Hangar is open, even with this window closed — but not while the tab is shut.',

    /** A ring, on screen and in §13. */
    ringingTitle: (label: string) => (label.trim().length === 0 ? 'Alarm' : label),
    ringingBody: (hour: number, minute: number) =>
      `It's ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`,
    ringingLate: (lateMs: number) => {
      const minutes = Math.floor(lateMs / 60_000);
      if (minutes < 60) return `This was due ${minutes} min ago`;
      const hours = Math.floor(minutes / 60);
      return hours === 1 ? 'This was due an hour ago' : `This was due ${hours} hours ago`;
    },
    dismiss: 'Dismiss',
    snooze: 'Snooze 9 min',

    /** The stopwatch. */
    start: 'Start',
    stop: 'Stop',
    resume: 'Resume',
    lap: 'Lap',
    reset: 'Reset',
    noLaps: 'No laps yet.',
    lapNumber: (n: number) => `Lap ${n}`,
  },

  /**
   * §27 — Reminders.
   *
   * The strings that carry the design are the sharing ones. "Only a connection" and "only the owner
   * can share" are not error messages the user caused — they are the rules, said where somebody
   * runs into them, because a greyed-out control with no explanation reads as a bug.
   */
  reminders: {
    title: 'Reminders',
    /** The sidebar. */
    myLists: 'My lists',
    sharedWithMe: 'Shared with me',
    newList: 'New list',
    listNamePlaceholder: 'List name',
    noLists: 'No lists yet. Make one.',
    openCount: (n: number) => String(n),
    sharedMark: 'Shared',
    ownedBy: (name: string) => `from ${name}`,

    /** The list. */
    addPlaceholder: 'Add a reminder',
    add: 'Add',
    notesPlaceholder: 'Notes',
    duePlaceholder: 'Due',
    noItems: 'Nothing here yet.',
    noneSelected: 'Pick a list.',
    completedBy: (name: string) => `done by ${name}`,
    completedByYou: 'done by you',
    /** §19.4 — both destructive actions confirm, and both say what is actually lost. */
    confirmDeleteList: (name: string) =>
      `Delete “${name}”? Everything in it goes, for everyone it is shared with.`,
    confirmDeleteItem: 'Delete this reminder?',
    deleteList: 'Delete list',
    rename: 'Rename',
    remove: 'Delete',
    readOnly: 'You can read this list but not change it',

    /** Sharing. */
    share: 'Share',
    shareTitle: (name: string) => `Who can see “${name}”`,
    sharedWithNobody: 'Only you.',
    addPerson: 'Add a connection',
    addGroup: 'Add a group',
    canEdit: 'Can edit',
    canView: 'Can view only',
    unshare: 'Remove',
    onlyConnections: 'You can only share with a connection.',
    onlyOwnerShares: 'Only the owner can share this list.',
    noConnections: 'No connections yet — invite someone from Connections.',

    /** Groups. */
    groups: 'Groups',
    groupsTitle: 'Your groups',
    newGroup: 'New group',
    groupNamePlaceholder: 'Group name',
    noGroups: 'No groups yet. A group is a set of connections you share with as one.',
    members: (n: number) => (n === 1 ? '1 member' : `${n} members`),
    addMember: 'Add',
    confirmDeleteGroup: (name: string) =>
      `Delete “${name}”? Every list shared with it stops being shared.`,
    deleteGroup: 'Delete group',
    close: 'Close',

    /** Due dates. */
    overdue: 'Overdue',
    dueToday: 'Today',
    dueSoon: 'This week',
    dueLater: 'Later',
  },

  /** §11.11 — the Recycle Bin as an app of its own. */
  recycleBin: {
    empty: 'The Recycle Bin is empty',
    restore: 'Restore',
    restoreAll: 'Restore everything',
    deleteForever: 'Delete forever',
    emptyBin: 'Empty Recycle Bin',
    originalPath: 'Original location',
    deletedAt: 'Deleted',
    restored: (name: string) => `${name} is back where it was`,
    usage: (items: number, size: string) =>
      `${items === 1 ? '1 item' : `${items} items`}, ${size}`,
  },

  /** §11.8 — the PDF reader. */
  pdf: {
    empty: 'Open a PDF to read it',
    unreadable: 'That PDF could not be read',
    loading: 'Reading…',
    previousPage: 'Previous page',
    nextPage: 'Next page',
    pageLabel: 'Page',
    of: (total: number) => `of ${total}`,
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    fit: 'Fit width',
    download: 'Download',
    search: 'Find in document',
    searchPlaceholder: 'Find',
    searchNext: 'Next match',
    searchPrevious: 'Previous match',
    matches: (index: number, total: number) => (total === 0 ? 'No matches' : `${index} of ${total}`),
    /**
     * D-170 — Hangar renders a PDF's text, not its graphics. A page whose content is a scan says
     * so plainly instead of showing a blank sheet and letting the reader wonder.
     */
    noTextOnPage: 'This page has no text to show — download it to see the original',
    encrypted: 'This PDF is protected, so its contents can’t be read here',
    unsupported: (detail: string) => `Part of this PDF uses ${detail}, which can’t be read here`,
  },

  /** §11.4 — the Browser. */
  browser: {
    newTab: 'New tab',
    closeTab: 'Close tab',
    newWindow: 'New window',
    back: 'Back',
    forward: 'Forward',
    reload: 'Reload',
    addressLabel: 'Address',
    addressPlaceholder: 'Search or enter address',
    bookmark: 'Bookmark this page',
    bookmarked: 'Remove bookmark',
    blank: 'New tab',
    pageTitle: {
      home: 'New tab',
      history: 'History',
      bookmarks: 'Bookmarks',
      about: 'About Hangar Browser',
    } as Record<string, string>,
    homeHeading: 'Where to?',
    historyHeading: 'History',
    historyEmpty: 'Nothing here yet',
    bookmarksHeading: 'Bookmarks',
    bookmarksEmpty: 'No bookmarks yet',
    aboutHeading: 'About Hangar Browser',
    /** D-015 / D-016 — said once, plainly, on the About page. */
    aboutBody:
      'Pages render in a frame on their own origin. Hangar never fetches a site on your behalf, ' +
      'so a site that refuses to be embedded is offered in a real browser tab instead.',
    /** §11.4 — the interstitial, under the PLAN-bound headline. */
    framingBody: (host: string) => `${host} asks not to be shown inside another page.`,
  },

  /** §11.5 — paste-a-URL install, disabled for guests (D-024, D-025). */
  storePasteUrlGuest: 'Available when signed in',
  /** §11.5 / §19.4 — Uninstall "removes and drops cached data after a confirm". */
  storeUninstallConfirm: (name: string) =>
    `Uninstall ${name}? Its windows will close and its cached data will be removed.`,

  /** §10.3 — Share / Send-to. PLAN names the behaviour, not the wording. */
  share: {
    title: 'Share with',
    cancel: 'Cancel',
    running: 'Already open',
  },
  shareNoTargets: 'No installed app accepts this kind of content',

  /** §10.1 — the clipboard-history panel, opened on the skin's chord (§6.4). */
  clipboard: {
    title: 'Clipboard history',
    empty: 'Nothing copied yet',
  },

  /** §12 — what each search group is called. The grouping is the shell's, so the names are too. */
  search: {
    app: 'Apps',
    file: 'Files',
    window: 'Open windows',
    setting: 'Settings',
  },

  /** §13 — the Notification Center, opened on the skin's chord (§6.4) or from the tray. */
  notifications: {
    title: 'Notifications',
    empty: 'No new notifications',
    /** §13 — "Clear all" per group, so the label names the group it belongs to. */
    clearGroup: (app: string) => `Clear all from ${app}`,
    clearAll: 'Clear all',
    doNotDisturb: 'Do not disturb is on',
  },

  /** §11.2 — the terminal's answer to an unresolved command. */
  commandNotFound: (cmd: string) => `${cmd}: command not found`,

  // §6.1 — window and desktop chrome, shared by every skin. A skin decides what a control looks
  // like; what it is *called* is the same sentence in all three, and belongs here rather than
  // inline in ten components (D-098).
  chrome: {
    minimize: 'Minimize',
    maximize: 'Maximize',
    restore: 'Restore',
    close: 'Close',
    unsaved: 'Unsaved changes',
    busy: 'Busy',
    /** §19.4 — a dialog's two default buttons. The *order* is the skin's (§6.3), not the copy's. */
    ok: 'OK',
    cancel: 'Cancel',
    openLauncher: 'Open launcher',
    personalize: 'Personalize',
    /** §6.3 — the Windows skin's own names for the same two things. */
    start: 'Start',
    openStart: 'Open Start',
    taskView: 'Task View',
    /** §6.3 — the same two surfaces, called what this skin's platform calls them. */
    missionControl: 'Mission Control',
    switchWindow: 'Switch window',
    back: 'Back',
    /** §6.3 / §12 — the Start menu's own sections. */
    startSearch: 'Search for apps and files',
    startNoResults: 'No results',
    pinned: 'Pinned',
    allApps: 'All apps',
    recommended: 'Recommended',
    recommendedEmpty: 'Recent files will show up here',
    /** §6.3 — the shell's own trailing jump-list entry, on every running app (D-100). */
    closeAllWindows: 'Close all windows',
    /** §5.4 — a Snap Group's taskbar entry, and the chooser that fills the rest of a layout. */
    snapGroup: 'Snap group',
    snapAssist: 'Choose a window for this space',
    openWindows: 'Open windows',
    noOpenWindows: 'No open windows',
    desktops: 'Desktops',
    newDesktop: 'New desktop',
    dismiss: 'Dismiss',
    /** §6.3 — what the macOS global menu bar says when no window has focus. */
    desktopName: 'Hangar',
    notifications: 'Notifications',
    noNotifications: 'No new notifications',
    snappedWindows: (count: number) =>
      count === 1 ? '1 snapped window' : `${count} snapped windows`,
  },

  // §11.7 (D-276) — the desktop as a folder: its context menu, its prompts, and the two states an
  // icon can be in that need explaining. PLAN does not name this surface's wording, so it is
  // authored here.
  desktop: {
    addApp: 'Add app to desktop…',
    addAppTitle: 'Which app?',
    addAppEmpty: 'Every app is already on the desktop',
    /** D-394 — shown while the walk is still running, so "nothing" is never guessed. */
    addAppReading: 'Looking…',
    newFolder: 'New folder',
    newFolderName: 'New folder',
    rename: 'Rename',
    renameTitle: (name: string) => `Rename “${name}” to:`,
    remove: 'Remove from desktop',
    open: 'Open',
    /** The prompt a drag-onto-drop raises. The default is what the folder is called if they just
     *  press Enter — naming it is the point of the gesture, so the field is never empty. */
    groupTitle: 'Name this group:',
    groupDefault: 'New group',
    /** §24.11 — a shortcut whose app is gated or gone. Said on the icon's tooltip, not a dialog. */
    unresolved: 'Sign in to use this app, or remove the icon',
    /** The empty desktop. Not an error — a fresh space with the one instruction that fills it. */
    empty: 'Right-click to add an app',
    /** On the back button while a folder is open: it is a drop target as well as a way out. */
    moveOut: 'Drop here to move out of this folder',
    /** D-285 — the group the Arcade titles start in, rather than thirteen deep in the alphabet. */
    arcadeFolder: 'Arcade',
    /** D-286 — back to the default arrangement. Destructive enough to ask (§19.4). */
    reset: 'Reset desktop',
    resetConfirm:
      'Put the desktop back to its default layout? Everything on it goes to the Recycle Bin, where you can get it back.',
  },

  // §7.8 / §19.4 — the only destructive operations in the VFS. PLAN names the Recycle Bin's
  // behaviour ("restore-to-original-path, empty") but not its wording, so these are authored
  // here rather than quoted from it.
  deleteForever: (name: string) => `Permanently delete “${name}”? This can’t be undone.`,
  emptyTrash: (count: number) =>
    count === 1
      ? 'Permanently delete 1 item from the Recycle Bin? This can’t be undone.'
      : `Permanently delete ${count} items from the Recycle Bin? This can’t be undone.`,

  // §11.1 — the File Explorer. PLAN names its behaviour, not its wording, so these are authored
  // here; the point of the module is that they are all in one place, not that they are quoted.
  explorer: {
    home: 'Home',
    apps: 'Apps',
    recycleBin: 'Recycle Bin',
    quickAccess: 'Quick access',
    back: 'Back',
    forward: 'Forward',
    up: 'Up one level',
    newFolder: 'New folder',
    newFile: 'New text document',
    newFolderName: 'New folder',
    newFileName: 'New file.txt',
    newWindow: 'New window',
    open: 'Open',
    rename: 'Rename',
    delete: 'Delete',
    cut: 'Cut',
    copy: 'Copy',
    paste: 'Paste',
    restore: 'Restore',
    deleteForever: 'Delete permanently',
    emptyBin: 'Empty Recycle Bin',
    emptyFolder: 'This folder is empty',
    emptyBinNotice: 'The Recycle Bin is empty',
    close: 'Close',
    download: 'Download',
    imported: (count: number) => (count === 1 ? '1 item imported' : `${count} items imported`),
    importFailed: (count: number) =>
      count === 1 ? '1 item couldn’t be imported' : `${count} items couldn’t be imported`,
    openWith: 'Open with',
    /** §10.3 — hands the content to another app; distinct from "Open with" (D-137). */
    share: 'Share…',
    shared: 'Shared',
    shareCancelled: 'Share cancelled',
    openWithNone: 'No app is registered',
    versionHistory: 'Version history',
    noVersions: 'No versions yet',
    restoreVersion: 'Restore this version',
    versionOf: (index: number, total: number) => `Version ${index} of ${total}`,
    peek: 'Peek',
    peekClose: 'Close preview',
    peekNoPreview: 'No preview for this kind of file',
    peekTooLarge: (bytes: number) => `Too large to preview (${Math.round(bytes / 1_000_000)} MB)`,
    viewList: 'List',
    viewGrid: 'Grid',
    viewColumn: 'Columns',
    columnName: 'Name',
    columnSize: 'Size',
    columnModified: 'Modified',
    deletedFrom: (path: string) => `from ${path}`,
    pasteFailed: (count: number) =>
      count === 1 ? '1 item couldn’t be pasted' : `${count} items couldn’t be pasted`,
  },

  // §11.3 — Notepad.
  notepad: {
    untitled: 'Untitled',
    newWindow: 'New window',
    newTab: 'New',
    open: 'Open…',
    save: 'Save',
    saveAs: 'Save as…',
    closeTab: (name: string) => `Close ${name}`,
    find: 'Find',
    findPlaceholder: 'Find',
    replacePlaceholder: 'Replace with',
    findNext: 'Next',
    findPrevious: 'Previous',
    replace: 'Replace',
    replaceAll: 'Replace all',
    matchCase: 'Match case',
    wordWrap: 'Word wrap',
    history: 'History',
    historyEmpty: 'No versions yet',
    restore: 'Restore',
    unsaved: 'Not saved yet',
    saving: 'Saving…',
    savedAt: (when: string) => `Saved ${when}`,
    noMatches: 'No matches',
    matchCount: (index: number, total: number) => `${index} of ${total}`,
    replacedCount: (count: number) =>
      count === 1 ? '1 replacement' : `${count} replacements`,
    position: (line: number, column: number) => `Ln ${line}, Col ${column}`,
    discard: (name: string) => `“${name}” has unsaved changes. Close it anyway?`,
    // §34.3 (M57.2) — the markdown preview pane.
    preview: 'Preview',
    previewEmpty: 'Nothing to preview yet',
  },

  // §11.13 — the Console (D-191).
  console: {
    screen: 'Console',
    newWindow: 'New window',
    sourcesLabel: 'Sources',
    levelLabel: 'Level',
    findPlaceholder: 'Filter',
    pause: 'Pause',
    resume: 'Resume',
    copy: 'Copy',
    export: 'Export',
    clear: 'Clear',
    following: 'Following',
    paused: 'Scrolled back',
    empty: 'Nothing logged yet',
    noMatches: 'Nothing matches this filter',
    counted: (shown: number, total: number) =>
      shown === total ? `${total} entries` : `${shown} of ${total} entries`,
    truncated: (hidden: number) =>
      hidden === 1
        ? '1 older entry above — narrow the filter to see it'
        : `${hidden} older entries above — narrow the filter to see them`,
    copied: (count: number) => (count === 1 ? 'Copied 1 entry' : `Copied ${count} entries`),
    copyFailed: 'Couldn’t copy',
    exported: (path: string) => `Exported to ${path}`,
    exportFailed: 'Couldn’t export',
  },

  /** §24.10 — the Arcade's shared HUD. Binding as written (PLAN §7.1). */
  game: {
    paused: 'Paused',
    /** Deliberately not "click to resume": resume needs a real gesture (§24.3). */
    resumeHint: 'Press any key to resume',
    gameOver: 'Game over',
    score: (value: number) => `Score ${value.toLocaleString()}`,
    best: (value: number) => `Best ${value.toLocaleString()}`,
    newBest: 'New best',
    playAgain: 'Play again',
    share: 'Share',
    close: 'Close',
    /** Shown when a run was restored mid-play from the §5.6 blob — it opens paused, never live. */
    resumed: 'Picking up where you left off',
    undo: 'Undo',
    newGame: 'New game',
    /** §25.3 — the win panel's offer. Binding: reaching 2048 must never end the game. */
    keepGoing: 'Keep going',
    youWin: 'You made 2048',
    hint2048: 'Arrow keys or swipe to move · Z to undo',
    /** §25.4 — Cascade's two modes. */
    marathon: 'Marathon',
    sprint: 'Sprint',
    scoreLabel: 'Score',
    linesLabel: 'Lines',
    levelLabel: 'Level',
    /** §25.5 — Belt's HUD and its one line of instructions. */
    livesLabel: 'Ships',
    waveLabel: 'Wave',
    hintBelt: 'Arrows to turn and thrust · Space to fire · H for hyperspace',
    /** §25.6 — Intercept names both input paths, because both are supported. */
    citiesLabel: 'Cities',
    hintIntercept: 'Aim with the mouse or the arrows · click, or Z / X / C to fire a battery',
    /** §29.7 — Phalanx. `livesLabel` says "Ships"; a cannon is not a ship, so this one is its own. */
    phalanxLivesLabel: 'Cannons',
    waveClear: 'Wave clear',
    hintPhalanx: 'Arrows or A / D to move · Space or Z to fire · one shot at a time',
    /** §24.9 — the title screen the attract demo plays behind. */
    phalanxTitle: 'PHALANX',
    phalanxStart: 'Press any key to start',
    /** §29.8 — Rivet. Its own lives label: a climber is not a ship and not a cannon. */
    rivetLivesLabel: 'Climbers',
    bonusLabel: 'Bonus',
    rivetClear: 'Floor clear',
    rivetTitle: 'RIVET',
    rivetStart: 'Press any key to start',
    hintRivet: 'Arrows or WASD to move and climb · Space to jump · the bonus clock is lethal',
    /** §29.9 — Burrow. */
    burrowLivesLabel: 'Diggers',
    roundLabel: 'Round',
    burrowClear: 'Field clear',
    burrowTitle: 'BURROW',
    burrowStart: 'Press any key to start',
    hintBurrow: 'Arrows or WASD to dig · hold Space to pump · undermine a rock and stand clear',
    /** §29.10 — Vines. */
    vinesLivesLabel: 'Climbers',
    boardLabel: 'Board',
    vinesClear: 'Board clear',
    vinesTitle: 'VINES',
    vinesStart: 'Press any key to start',
    hintVines: 'Arrows or WASD to climb · Space to jump · two vines hold you, one lets go',
    /** §29.11 — Sortie. */
    sortieLivesLabel: 'Planes',
    stageLabel: 'Stage',
    sortieClear: 'Stage clear',
    sortieTitle: 'SORTIE',
    sortieStart: 'Press any key to start',
    hintSortie: 'Arrows or WASD to fly · Space to fire · Z to loop out of trouble',
    /**
     * §32 — Timber.
     *
     * "Tower" rather than "Level": `levelLabel` is Cascade's word for a speed, and a player reading
     * both in one Arcade should not have to work out that they mean different things.
     */
    timberTowerLabel: 'Tower',
    timberBlocksLabel: 'Blocks',
    timberMissesLabel: 'Misses',
    timberHint: (left: number) => `Hint (${left})`,
    timberRestart: 'Restart tower',
    timberCleared: 'Tower cleared',
    timberNext: 'Next tower',
    /** Shown on a tap that moved nothing: the block is flush against the one that stopped it. */
    timberBlocked: 'Wedged — it is already against the block behind it',
    /**
     * §32.1 (D-446) — shown when a tap **moved** a block without clearing it.
     *
     * The sentence has to carry the cost, because the picture does not: a block that slid three
     * cells and stopped looks like something that worked. It did not, and it may have wedged the
     * tower for good.
     */
    timberSlid: 'It slid until it hit — that block is somewhere new now',
    timberSlidLabel: 'Slid',
    /** §32.1 (D-446) — total deadlock: blocks left, and not one of them can move. */
    timberStuck: 'Nothing can move — undo, or restart the tower',
    hintTimber: 'Drag to turn the tower · a block slides until it hits, so tap the clear ones',
    /** §25.7 — Chomp. */
    hintChomp: 'Arrows or WASD to turn · eat the pellets to turn the tables',
    /** §25.9 — Skipper. `livesLabel` says "Ships", which is Belt's word, so this one is its own. */
    coinsLabel: 'Coins',
    timeLabel: 'Time',
    skipperLivesLabel: 'Lives',
    skipperClear: 'Level clear',
    skipperWon: 'You brought it home',
    hintSkipper:
      'Arrows or WASD to run and jump · hold jump to go higher · X to sprint · Z to throw · Down to enter a pipe',
    /**
     * §25.8 — Conquest.
     *
     * The phase names are the rules made visible: a player who cannot say which of the three they
     * are in cannot tell why a click did nothing, and this game has three different meanings for
     * clicking the same territory.
     */
    reinforce: 'Reinforce',
    attack: 'Attack',
    fortify: 'Fortify',
    armiesToPlace: (n: number) => (n === 1 ? '1 army to place' : `${n} armies to place`),
    endReinforce: 'Done placing',
    endAttack: 'Done attacking',
    endTurn: 'End turn',
    tradeSet: 'Trade a set',
    cardsHeld: (n: number) => (n === 1 ? '1 card' : `${n} cards`),
    mustTradeNow: 'You must trade a set before doing anything else',
    occupyTitle: (to: string) => `Move armies into ${to}`,
    occupyConfirm: 'Move in',
    fortifyConfirm: 'Move',
    conquestWon: (name: string) => `${name} holds the world`,
    yourTurn: (name: string) => `${name}’s turn`,
    thinking: 'Thinking…',
    hintReinforce: 'Click a territory to add one army · shift-click to place them all',
    hintAttack: 'Click one of your territories, then a neighbour to attack it',
    hintFortify: 'Click a territory to move armies from, then anywhere connected to it',
    /**
     * §25.8 (D-332) — the continents, and what holding one pays.
     *
     * The bonuses were in the rules from M25.1 and nowhere on the screen: `reinforcementFor` has
     * always been `max(3, ⌊t/3⌋)` **plus a bonus per continent held entire**, and a player could
     * read neither the boundaries, nor the price, nor how close they were. A reinforcement rule
     * nobody can see is a reinforcement rule nobody plays around, which is most of the game.
     */
    continentsTitle: 'Continents',
    continentBonus: (n: number) => `+${n}`,
    /** `4/9` — how much of it you hold. The number that says "two more and it pays". */
    continentShare: (held: number, total: number) => `${held}/${total}`,
    continentHeldBy: (name: string) => `held by ${name}`,
    continentContested: 'contested',
    /** The reinforcement readout, broken into the two things it is made of. */
    reinforcementBreakdown: (base: number, bonus: number) =>
      bonus === 0 ? `${base} from territories` : `${base} from territories · +${bonus} from continents`,
    newGameTitle: 'New game',
    hotSeat: 'Hot seat',
    versus: (level: string) => `vs ${level}`,
    levelEasy: 'Easy',
    levelStandard: 'Standard',
    levelHard: 'Hard',
    /** D-280 — a Conquest game is an hour, and both of these throw it away. */
    confirmRestart: 'Start a new game? The one in progress will be lost.',
    confirmDifficulty: (roster: string) =>
      `Switch to ${roster}? That starts a new game, and the one in progress will be lost.`,

    /**
     * §24.8 (D-309) — the save-slot bar. Substrate copy, not Wayfarer's, because the bar is the
     * host's and a second title declaring slots must not get a second vocabulary for them.
     */
    /**
     * §25.9 (D-329) — what the final jump paid.
     *
     * Spelled out as arithmetic rather than as a total, because the multiplier is the whole point:
     * a player who sees `84 × 50 × 3` learns that reaching the top of the pole tripled it, and a
     * player who sees `12600` learns nothing and aims at nothing next time.
     */
    skipperTimeBonus: (seconds: number, per: number, multiplier: number, total: number) =>
      multiplier === 1
        ? `Time ${seconds} × ${per} = ${total}`
        : `Time ${seconds} × ${per} × ${multiplier} = ${total}`,
    skipperTopOfThePole: 'Top of the pole!',

    slotsLabel: 'Saves',
    saveSlot: (label: string) => `Save ${label}`,
    loadSlot: (label: string) => `Load ${label}`,

    /**
     * §25.10 — Wayfarer.
     *
     * The world's own words — who says what, what a sword is called — are content and live in
     * `apps/wayfarer/data/` beside the map that places them (D-310). What is here is the *chrome*:
     * the four battle commands, the three menu pages, the shop and the inn. That is the line the
     * "copy lives in one module" rule is drawing — a rule that swallowed every NPC line would make
     * this file the game.
     */
    hpLabel: 'Health',
    mpLabel: 'Spirit',
    goldLabel: 'Gold',
    xpLabel: 'Experience',
    hintWayfarer:
      'Arrows or WASD to walk · Enter to speak, open and confirm · X to go back · Q for the pack',
    fight: 'Attack',
    spell: 'Spell',
    item: 'Item',
    flee: 'Flee',
    pageItems: 'Pack',
    pageSpells: 'Spells',
    pageGear: 'Gear',
    buy: 'Buy',
    sell: 'Sell',
    innOffer: (price: number) => `A bed for ${price} gold? Enter to rest, X to decline.`,
    slotOne: 'I',
    slotTwo: 'II',
    slotThree: 'III',
    wayfarerEnd: 'The ash is out',

    /**
     * §26.7 / §26.8 — multiplayer, and the two sentences a game has to be able to say.
     *
     * **`desyncTitle` is binding as written** (§26.7, §26.8 F13). It is the honest sentence for a
     * failure nobody can fix from inside the game: two clients that stopped agreeing cannot be
     * reconciled by trying harder, so the only offer is a fresh start. Saying "out of step" rather
     * than "connection lost" matters — the network is usually fine, and a player told to check it
     * would go and look at the wrong thing.
     */
    desyncTitle: 'This game got out of step',
    desyncRestart: 'Restart',
    /** §26.8 F11 — the peer went quiet. The session holds for ten minutes; the choice is theirs. */
    peerLeft: (name: string) => `${name} disconnected`,
    peerWait: 'Wait',
    peerEnd: 'End game',
    /**
     * §26.8 (D-443) — the peer **closed their window**, which is a different sentence from F11's.
     *
     * "Disconnected" invites waiting, and F11's panel offers exactly that. Somebody who closed the
     * game is not coming back to it, so this says so and offers one button. Getting the two mixed
     * up is what left a player watching a board for five minutes.
     */
    peerGone: (name: string) => `${name} left the game`,
    peerGoneClose: 'Close',
    /** What a client says while it is holding a session open for somebody who might come back. */
    peerWaiting: (name: string) => `Waiting for ${name}`,
    /** §26.4 — suggesting a game, from the roster. */
    suggestGame: 'Suggest a game',
    /** The two titles multiplayer covers (D-210), named where a person picks one. */
    suggestConquest: 'Conquest',
    suggestIntercept: 'Intercept',
    suggestChess: 'Chess',
    suggestSent: (name: string) => `Asked ${name} — waiting for them to join`,
    suggestFailed: 'Couldn’t start that game',
    /** The banner a networked game carries, so it is never ambiguous who you are playing. */
    playingWith: (name: string) => `Playing with ${name}`,
    yourMove: 'Your move',
    theirMove: (name: string) => `${name} is thinking`,
    waitingToJoin: (name: string) => `Waiting for ${name} to join`,
    theyDeclined: (name: string) => `${name} said no`,
    gameEnded: 'That game has ended',

    /**
     * §26.8 (D-586) — Connections' *Games in progress* list.
     *
     * The heading says "in progress" rather than "your games", because the point of the list is
     * that these are unfinished and waiting — the word is doing the work of the empty state.
     */
    liveGamesTitle: 'Games in progress',
    liveGameRow: (game: string, name: string) => `${game} with ${name}`,
    liveGameYourTurn: 'Your move',
    liveGameTheirTurn: 'Their move',
    liveGameInvited: 'Not started yet',
    liveGameResume: 'Resume',
    liveGameEnd: 'End game',
    /** §19.4 — ending a game ends it for the other player too, and the sentence says so. */
    liveGameEndConfirm: (game: string, name: string) =>
      `End this game of ${game} with ${name}? It ends for both of you and cannot be resumed.`,

    // ── §29 Arcade Wave 2 (PLAN §7.2). Binding as written; tests assert the literals. ────────
    /** §29.2 / §29.4 — the card titles' shared chrome. */
    movesLabel: 'Moves',
    dailyDeal: 'Daily deal',
    dailyBanner: (day: string) => `Daily deal · ${day}`,
    finish: 'Finish',
    continueGame: 'Continue',
    /** §29.2 — FreeCell's own line, shown when nothing can move. */
    noMovesLeft: 'No moves left',
    streakLabel: 'Streak',
    bestStreakLabel: 'Best streak',
    winRateLabel: 'Win rate',
    playedLabel: 'Played',
    /** §29.4 — Pyramid. */
    drawCard: 'Draw',
    redeal: 'Redeal',
    stockLabel: 'Stock',
    /** §30.6 — Standings, inside Connections. Binding: PLAN §7.2. */
    standings: 'Standings',
    todaysDeals: "Today's deals",
    headToHead: 'Head to head',
    noPlaysToday: 'No plays yet today.',
    noFinishedGames: 'No finished games yet.',
    /** A daily attempt that did not finish. Still a row: it is still an attempt at today's board. */
    unfinished: 'Unfinished',
    rematch: 'Rematch',
    /** §29.5 — Chess's meta-moves. */
    drawOffered: (name: string) => `${name} offers a draw`,
    accept: 'Accept',
    decline: 'Decline',
    offerDraw: 'Offer a draw',
    resign: 'Resign',
    resignConfirm: 'Resign this game?',
    checkmate: 'Checkmate',
    stalemate: 'Stalemate',
    /** §29.5 — the *result*, not §29.4's stock control (`drawCard`). */
    drawnGame: 'Draw',
    check: 'Check',
    flipBoard: 'Flip board',
    /** §29.6 — Backgammon's cube. */
    doublesTo: (name: string, value: number) => `${name} doubles to ${value}`,
    take: 'Take',
    drop: 'Drop',
    double: 'Double',
    rollDice: 'Roll',
    /** §29.6 — a turn with no legal move still has to be taken. */
    pass: 'Pass',
    suggestBackgammon: 'Backgammon',
    suggestSortie: 'Sortie',
    /** §29.12 — Pulse. Binding: PLAN §7.2. */
    practiceBanner: "Practice — attempts here don't count.",
    practiceMode: 'Practice',
    attemptsLabel: 'Attempts',
    progressLabel: 'Progress',
    pulseTitle: 'PULSE',
    pulseStart: 'Press any key to start',
    songLabel: 'Song',
    hintPulse: 'One button: Space or click to jump · hold to jump again on landing · C marks a practice checkpoint',
    /** §29.12 / §31.4 — a custom level file that will not compile says why, and the app stays up. */
    levelBad: (name: string) => `${name} could not be loaded`,
    customLevels: 'Your levels',
  },

  /** §26.3 / §26.4 — Connections, and the shell's invitation card. Binding as written. */
  social: {
    title: 'Connections',
    /** The four roster groups §26.3 names, in the order they are useful. */
    groupPendingIn: 'Waiting for you',
    groupAccepted: 'Connected',
    groupPendingOut: 'Invited',
    groupBlocked: 'Blocked',
    empty: 'Nobody yet. Send someone a link.',
    /** The Add flow: one button, one link, copied by hand. There is no directory to search. */
    add: 'Invite someone',
    linkLabel: 'Send them this link',
    linkExpires: (days: number) => (days === 1 ? 'Good for a day' : `Good for ${days} days`),
    copy: 'Copy',
    copied: 'Copied',
    copyFailed: 'Couldn’t copy — select the link and copy it',
    accept: 'Accept',
    decline: 'Decline',
    remove: 'Remove',
    block: 'Block',
    /** §19.4 — both destructive actions confirm, and both say what they actually do. */
    confirmRemove: (name: string) =>
      `Remove ${name}? You will both lose the connection, and either of you can invite the other again.`,
    confirmBlock: (name: string) =>
      `Block ${name}? The connection is removed and they cannot invite you again.`,
    /** Presence, in words rather than only a colour. */
    /**
     * §26.2 — every state, and **all** of them: `statusLabel` maps over `PresenceState` with no
     * fallthrough, so a state added to the model without a word here is a type error rather than a
     * peer who silently reads as "Online" (D-347).
     */
    online: 'Online',
    idle: 'Idle',
    busy: 'Busy',
    offline: 'Offline',
    playing: (game: string) => `Playing ${game}`,
    offlineNotice: 'Not connected — presence may be out of date',
    /** §26.4 — the invitation card. */
    inviteTitle: (name: string) => `${name} suggests a game`,
    inviteJoin: 'Join',
    inviteDecline: 'Decline',
    inviteMore: (n: number) => (n === 1 ? '+1 more' : `+${n} more`),
    /** §26.3 — what opening a `#/connect/<token>` link says when it lands. */
    connected: (name: string) => `You and ${name} are connected`,
    connectFailed: 'That invitation has expired',
    connectPending: 'Sign in to accept that invitation',
  },
  /**
   * §26.5 — Chat. 1:1, connections only, **text only**.
   *
   * The empty states carry the whole shape of the feature, so they say it outright rather than
   * apologising: there is no directory to search (§26.3), so the only way to reach somebody new is
   * a link, and the only people who can be written to are already on the list.
   */
  chat: {
    title: 'Chat',
    threadsTitle: 'Conversations',
    noThreads: 'Nobody to talk to yet. Invite someone from Connections.',
    noThread: 'Pick a conversation.',
    noMessages: 'Nothing here yet. Say something.',
    /** The thread-list preview for a message that was deleted, and the pane's own tombstone. */
    deleted: 'Message deleted',
    placeholder: 'Write a message',
    send: 'Send',
    earlier: 'Load earlier messages',
    searchLabel: 'Search this conversation',
    /** Client-side, over what has been loaded — which is the honest thing to say about it. */
    searchEmpty: (term: string) => `Nothing loaded here matches “${term}”`,
    remaining: (n: number) => `${n} left`,
    /** §19.4 — deleting confirms, and says what actually happens (D-218). */
    confirmDelete: 'Delete this message? It goes for both of you, and it cannot be brought back.',
    delete: 'Delete',
    unread: (n: number) => (n > 99 ? '99+' : String(n)),
    /** What each refusal §16 can answer with actually means to the person typing. */
    tooLong: 'That message is too long — 4096 characters is the limit',
    notConnected: 'You are not connected to that person any more',
    tooFast: 'Slow down a moment — that is a lot of messages',
    sendFailed: 'That message did not send',
  },

  /** §34.2 — Archive. The refusals are binding as written; tests assert the literals. */
  archive: {
    title: 'Archive',
    empty: 'Open a .zip from Explorer to look inside it.',
    loading: 'Reading the archive…',
    extracting: 'Extracting…',
    count: (n: number) => (n === 1 ? '1 file' : `${n} files`),
    extracted: (n: number, dir: string) =>
      `${n === 1 ? '1 file' : `${n} files`} extracted to ${dir}`,
    extractHere: 'Extract Here',
    extractAll: 'Extract All…',
    compress: 'Compress to zip',
    compressed: (name: string) => `Compressed to ${name}`,
    /** §34.2 — Peek's own line when a listing was truncated at the cap. */
    peekMore: (n: number) => `…and ${n} more`,
    encrypted: 'This archive is password-protected — Hangar can’t open those.',
    malformed: 'This file isn’t a zip archive Hangar can read.',
    /** Anything that is not the format's own refusal — a denied read, a worker that failed. */
    unreadable: 'That archive could not be opened.',
  },

  // ── §34.6 / §34.7 — Media (M60 local playback, M61 the YouTube library) ──────────────────
  //
  // One block for both milestones, because it is one app and one merge. Everything §34.6 and
  // §34.7 quote is **binding as written** and asserted by `media-copy.test.ts`; the apostrophes
  // are typographic, following M56's precedent (§34.2 writes `can't`, `COPY.archive.encrypted`
  // ships `can’t`, and the copy file is where house typography is applied).
  media: {
    title: 'Media',

    /** §34.6 — the left rail, in the order the spec lists it. */
    later: 'Watch Later',
    favorites: 'Favorites',
    files: 'Files',

    /** Empty states. The first two are M61's views seen before anything is in them. */
    laterEmpty: 'Paste a YouTube link below and it lands here.',
    favoritesEmpty: 'Videos you save from Watch Later show up here, grouped by category.',
    filesEmpty: 'Open an audio or video file from Explorer to play it.',

    /**
     * §34.6, binding — the one refusal local playback has. The browser is the decoder, so the
     * honest sentence names the codec and stops, rather than offering a conversion §22 has no
     * dependency for.
     */
    unsupportedCodec: (name: string) =>
      `This file’s codec (${name}) isn’t supported by this browser.`,

    /** Transport. Labels rather than glyph-only buttons, so the controls are reachable by name. */
    play: 'Play',
    pause: 'Pause',
    previous: 'Previous',
    next: 'Next',
    loop: 'Loop',
    speed: 'Speed',
    volume: 'Volume',
    speedLabel: (rate: number) => `${rate}×`,

    /** §34.6 — the mini-player, and the way back out of it. */
    mini: 'Mini player',
    miniExit: 'Full player',

    /**
     * §34.6 — Peek's metadata line for an audio or video file (M60.4).
     *
     * It lives in Media's block rather than Explorer's because §34.6 is where the requirement is,
     * and because three lanes are editing Explorer's block this wave. The codec is named from the
     * *extension*: `MediaError` carries no codec name, so anything more specific would be a guess
     * printed as a fact.
     */
    peekLine: (kind: 'audio' | 'video', duration: string, ext: string) =>
      `${kind === 'audio' ? 'Audio' : 'Video'} · ${duration} · ${ext.replace(/^\./, '')}`,

    /** §34.6 / D-496 — Explorer's badge for a file the sync layer will never carry. */
    deviceLocal: 'on this device',
    deviceLocalTitle: (thresholdMb: number) =>
      `Larger than ${thresholdMb} MB, so this file stays on this device and is never synced.`,

    // ── §34.7 — the YouTube library ────────────────────────────────────────────────────────

    /** §34.7 — the intake box takes text of any shape, so the placeholder says so. */
    intakePlaceholder: 'Paste YouTube links — one, or a whole wall of them',
    intakeAdd: 'Add',

    /**
     * §34.7, **binding**: "Added {n} · skipped {m} already queued · ignored {k} lines that
     * weren't YouTube."
     *
     * All three counts are always shown, including zeros. A summary that hid its zeros would make
     * "nothing happened" and "everything was already there" look identical, which is the one case
     * somebody pasting a wall of links actually needs told apart.
     */
    intakeSummary: (added: number, skipped: number, ignored: number) =>
      `Added ${added} · skipped ${skipped} already queued · ignored ${ignored} lines that weren’t YouTube.`,

    /** §34.7, binding — the save card, raised by `ended` or from a card's menu. */
    saveTitle: 'Save to favorites?',
    saveTitleField: 'Title',
    saveCategoryField: 'Category',
    saveNewCategory: 'New category…',
    save: 'Save',
    notNow: 'Not now',
    removeFromList: 'Remove from list',

    /** §34.7, binding — what the embed reporting 100/101/150 leaves on the card. */
    unavailable: 'This video is unavailable — it may be private or removed.',

    /** The library grid's filter row. */
    filterPlaceholder: 'Filter by title or channel',
    allCategories: 'All categories',
    uncategorized: 'Uncategorized',

    /** §34.7 — a guest, or a signed-in user whose oEmbed lookup has not answered yet. */
    untitled: (id: string) => `youtube.com/watch?v=${id}`,
  },

  // ── §34.4 Quick Notes (M58) ──────────────────────────────────────────────────────────────
  notes: {
    title: 'Quick Notes',
    /** The capture card's window title — it is a window, so the frame says what it is. */
    captureTitle: 'Quick note',
    capturePlaceholder: 'Write a note — Enter saves, Shift+Enter for a new line, Esc discards',
    /** The card's own footer, so the three keys are visible after the placeholder is typed over. */
    captureHint: 'Enter saves · Shift+Enter new line · Esc discards',
    untitled: 'Empty note',
    pinnedMissing: 'That note is no longer there.',
    empty: 'No notes yet. Press the capture chord from anywhere to write one.',
    filterPlaceholder: 'Filter notes',
    noMatches: 'Nothing matches this filter',
    openInNotepad: 'Open in Notepad',
    pin: 'Pin',
    unpin: 'Unpin',
    pinned: 'Pinned',
    saveFailed: 'Couldn’t save that note',
    counted: (shown: number, total: number) =>
      shown === total
        ? total === 1
          ? '1 note'
          : `${total} notes`
        : `${shown} of ${total} notes`,
  },

  // ── §34.5 Diff (M59) ─────────────────────────────────────────────────────────────────────
  diff: {
    title: 'Diff',
    empty: 'Choose two text files to compare.',
    chooseLeft: 'Choose the first file…',
    chooseRight: 'Choose the second file…',
    sideBySide: 'Side by side',
    unified: 'Unified',
    identical: 'These two files are identical.',
    /** §34.5, binding as written. */
    binary: 'That’s a binary file — Diff compares text.',
    tooLarge: (size: string) => `Too large to diff (${size}).`,
    unreadable: 'That file could not be read.',
    /** The version scrubber's entry point, and the labels the two sides carry. */
    compare: 'Compare with…',
    comparePinned: 'Comparing from here',
    compareAgainstPinned: 'Compare from here instead',
    compareTwo: 'Compare the two',
    compareWithCurrent: 'Compare with current',
    current: 'current',
    versionLabel: (index: number, total: number) => `version ${index} of ${total}`,
    summary: (added: number, removed: number) => `+${added} −${removed}`,
  },

  // ── Lane C — M62 Voice Memos · M63 Snip · M64 Calendar · M65 Weather (§34.9–§34.12).
  //
  // Kept as one contiguous block so the merge back into this file is mechanical (D-512). Every
  // string §34 quotes is here verbatim and asserted as a literal; the rest is labelling.

  /** §34.9 — Voice Memos. The denied-permission sentence is binding as written. */
  voice: {
    title: 'Voice Memos',
    record: 'Record',
    stop: 'Stop',
    empty: 'No memos yet — press Record to make one.',
    /** §34.17 — a denied microphone is a refusal, and the app stays usable for existing memos. */
    denied: 'Hangar needs microphone access to record — allow it in your browser and try again.',
    recording: 'Recording…',
    /** §34.9 — the ● the window title carries while recording. */
    titleRecording: (name: string) => `● ${name}`,
    level: 'Input level',
    play: 'Play',
    pause: 'Pause',
    rename: 'Rename',
    renamePrompt: 'New name for this memo',
    delete: 'Delete',
    confirmDelete: (name: string) => `Delete ${name}? It goes to the Recycle Bin.`,
    saved: (name: string) => `Saved ${name}`,
    /** §34.17 — what the next launch says about a recording a crash interrupted. */
    recovered: (name: string) => `Recovered ${name} from an interrupted recording`,
    count: (n: number) => (n === 1 ? '1 memo' : `${n} memos`),
    unreadable: 'That memo could not be played.',
  },

  /** §34.10 — Snip. The cancelled sentence is binding as written. */
  snip: {
    title: 'Snip',
    modeRegion: 'Region snip',
    modeWindow: 'Window snip',
    modeFull: 'Full desktop',
    /** The picker overlay's own instruction, per mode. */
    hintRegion: 'Drag to choose a region · Esc to cancel',
    hintWindow: 'Click a window to capture it · Esc to cancel',
    hintFull: 'Click anywhere to capture the whole desktop · Esc to cancel',
    empty: 'Pick a mode to take a snip.',
    capturing: 'Waiting for the capture prompt…',
    /** §34.17 — `getDisplayMedia` denied **or** cancelled: no capture, no partial file. */
    cancelled: 'Capture cancelled — nothing was taken.',
    tools: 'Annotate',
    arrow: 'Arrow',
    box: 'Box',
    highlight: 'Highlight',
    text: 'Text',
    crop: 'Crop',
    textPrompt: 'Text to add',
    undo: 'Undo',
    save: 'Save',
    copy: 'Copy',
    share: 'Share',
    savedTitle: 'Snip saved',
    savedBody: (name: string) => `${name} is in your Screenshots folder.`,
    copied: 'Copied to the clipboard.',
    shared: 'Shared.',
    shareCancelled: 'Not shared.',
  },

  /** §34.11 — the calendar half of Clock & Calendar. The import notice is binding as written. */
  calendar: {
    events: 'Events',
    newEvent: 'New event',
    editEvent: 'Edit event',
    titleLabel: 'Title',
    titlePlaceholder: 'What is it?',
    startLabel: 'Starts',
    endLabel: 'Ends',
    allDay: 'All day',
    notesLabel: 'Notes',
    repeatLabel: 'Repeats',
    repeatNever: 'Never',
    repeatDaily: 'Daily',
    repeatWeekly: 'Weekly',
    repeatMonthly: 'Monthly',
    repeatYearly: 'Yearly',
    intervalLabel: 'Every',
    untilLabel: 'Until',
    reminderLabel: 'Remind me',
    reminderNone: 'No reminder',
    reminderAt: (minutes: number) =>
      minutes === 0
        ? 'At the time of the event'
        : minutes % 1440 === 0
          ? `${minutes / 1440} day${minutes === 1440 ? '' : 's'} before`
          : minutes % 60 === 0
            ? `${minutes / 60} hour${minutes === 60 ? '' : 's'} before`
            : `${minutes} minutes before`,
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    confirmDelete: (title: string) => `Delete “${title}”? Every repeat of it goes too.`,
    nothingOnDay: 'Nothing on this day',
    overflow: (n: number) => `+${n}`,
    /**
     * §34.11's stated behaviour, said where somebody choosing it can see it.
     *
     * The skip is not a defect and the spec says so, but a user picking "monthly" on the 31st has no
     * way to know that unless the form tells them — and finding out in April is finding out too late.
     */
    shortMonthHint: (day: number) =>
      `Months shorter than ${day} days are skipped — this repeats only in months with a ${day}${
        day === 31 ? 'st' : day === 30 ? 'th' : 'th'
      }.`,
    exportIcs: 'Export .ics',
    importIcs: 'Import .ics…',
    exported: (name: string) => `Exported ${name}`,
    /** §34.11 — binding: the batch notice every import shows, counts included. */
    imported: (n: number, m: number) =>
      `${n} events imported · ${m} had repeat rules Hangar doesn’t support and were imported as ` +
      'single events.',
    importFailed: 'That file isn’t a calendar Hangar can read.',
    /** §34.11 — a reminder, as §13 posts it. */
    reminderTitle: (title: string) => title,
    reminderBody: (at: string) => `Starts at ${at}`,
    allDayBody: (date: string) => `All day on ${date}`,
  },

  /** §34.12 — Weather. Both refusal sentences are binding as written. */
  weather: {
    title: 'Weather',
    empty: 'Add a location to see its forecast.',
    addLocation: 'Add location',
    /**
     * D-588 — one box, and the placeholder is the documentation.
     *
     * It names all three things the box takes, in the order somebody would try them, because there
     * is nowhere else in this app to say so and a field labelled just "Place" would leave the
     * airport code — the whole reason this was built — undiscoverable.
     */
    queryPlaceholder: 'City, state or airport code — Hartford, CT · KHFD',
    search: 'Search',
    searching: 'Searching…',
    /** D-588 — a successful search that found nothing. Not the same as a provider being down. */
    noMatches: (query: string) => `Nothing matches “${query}”.`,
    searchUnreachable: 'Place search is unreachable right now.',
    /** D-588 — the airport hint on a result row, where the country would otherwise go. */
    airport: 'Airport',
    /**
     * D-589 — ODbL attribution, which Nominatim's terms require wherever its answers are shown.
     *
     * In the add panel rather than the window's footer: that is where a searched-for place is
     * displayed, and an attribution that follows the data is one somebody can actually connect to
     * what it credits.
     */
    attribution: 'Places from Open-Meteo · names © OpenStreetMap contributors',
    add: 'Add',
    remove: 'Remove',
    confirmRemove: (name: string) => `Remove ${name} from your locations?`,
    useMyLocation: 'Use my location',
    /** The name a geolocated place gets when nothing could name it (D-589's fallback). */
    myLocation: 'My location',
    locateFailed: 'Your browser would not share your location.',
    badCoords: 'That is not a latitude and longitude.',
    loading: 'Loading the forecast…',
    /** §34.12 — binding: a 503 with an empty cache. */
    unreachable: 'Weather is unreachable right now.',
    /** §34.12 — binding: stale data renders with its age. */
    stale: (age: string) => `as of ${age} ago`,
    humidity: (pct: number) => `${pct}% humidity`,
    wind: (text: string) => `Wind ${text}`,
    precip: (pct: number) => `${pct}%`,
    refresh: 'Refresh',
  },

  /**
   * §34.13 (M66) — Send to connection. **Binding as written**; the tests assert the literals.
   *
   * Two of these are the 409 sentences §34.13 spells out and neither is negotiable: they are the
   * only thing the person clicking ever sees of a refusal, and both name what is actually wrong
   * rather than apologising. The rest follow §26's register — short, present tense, and about the
   * person rather than about the request.
   */
  offers: {
    /** The Explorer context item, and the §10.3 send-to verb it sits beside. */
    send: 'Send to connection…',
    /** The shell-drawn roster picker (§19.4 — the app never draws this or reads the roster). */
    pickTitle: 'Send to',
    pickSummary: (name: string, size: string) => `${name} · ${size}`,
    pickCancel: 'Cancel',
    pickEmpty: 'Nobody to send to yet. Invite someone from Connections.',
    /** What Explorer's status line says once the picker has closed. */
    sent: (name: string, to: string) => `Sent ${name} to ${to}`,
    sendCancelled: 'Not sent',
    /** §34.13, binding: a file the sync layer never uploaded (D-496) cannot be offered. */
    deviceLocal: 'This file lives only on this device — it can’t be sent.',
    notConnected: 'You are not connected to that person any more',
    sendFailed: 'That file did not send',

    /** §34.13, binding — the invitation card on D-214's surface. */
    cardTitle: (user: string, name: string, size: string) => `${user} sent you ${name} (${size})`,
    accept: 'Accept',
    decline: 'Decline',
    more: (n: number) => (n === 1 ? '+1 more' : `+${n} more`),
    /** §34.13, binding: the accept refusal. The offer stays pending (§34.17). */
    noSpace: (size: string) => `Not enough space — this file needs ${size}.`,
    acceptFailed: 'That file could not be accepted',
    /** Where it landed, once it has. */
    accepted: (name: string) => `${name} is in your Downloads`,

    /** §34.13 — the sender's §13 notification, on their next session. */
    resolvedTitle: 'Send to connection',
    theyAccepted: (user: string, name: string) => `${user} accepted ${name}`,
    theyDeclined: (user: string, name: string) => `${user} declined ${name}`,
  },
} as const;
